import { NewRSU } from '_lib/api';
import { RootState, useAppDispatch } from '_store';
import { devicesSagas } from '_store/devices/sagas';
import { devicesSelector } from '_store/devices/selectors';
import manageRsuFormReducer, { ManageRsuForm } from '_store/forms/manage-rsu';
import { roadsSelector, selectedRoadSegmentIdsSelector } from '_store/roads/selectors';
import { useLocalization } from '@fluent/react';
import { Button, CardContent, TextField, Tooltip } from '@material-ui/core';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { useRolePermissions } from '../../../hooks/useRolePermissions';
import { RoleGuard } from '../../items/RoleGuard';
import SelectItem from '../../items/SelectItem';
import { toLatLng } from '../../routes/MapView/utils';
import styles from './styles';
import { CrudTypeEnum, RsuManageType } from './types';

// TODO Replace this with a proper validation library (when we have more time)
const isTextValueValid = (value?: string) =>
  value !== undefined && value !== null && value.length > 0;

const isNumberValid = (number: number) =>
  number !== undefined && number !== null && !Number.isNaN(number);

const isLatitude = (lat: number) =>
  isNumberValid(lat) && Number.isFinite(lat) && Math.abs(lat) <= 90;

const isLongitude = (lng: number) =>
  isNumberValid(lng) && Number.isFinite(lng) && Math.abs(lng) <= 180;

const isFormValid = (form: ManageRsuForm): boolean =>
  isTextValueValid(form.name) &&
  isLatitude(form.latitude) &&
  isLongitude(form.longitude);

const RsuManage: FC<RsuManageType> = ({ id, type }) => {
  const { l10n } = useLocalization();
  const classes = styles();
  const dispatch = useAppDispatch();
  const { canManageRsu, blockedActionMode } = useRolePermissions();
  const [touched, setTouched] = useState({
    name: false,
    roadSegmentId: false,
    latitude: false,
    longitude: false,
  });

  const roads = useSelector(roadsSelector);
  const devices = useSelector(devicesSelector);
  const mapRef = useSelector((state: RootState) => state.application.mapRef);
  const selectedRoadSegmentIds: string[] = useSelector(selectedRoadSegmentIdsSelector);
  const formState = useSelector((state: RootState) => state.forms['manage-rsu']);

  const { busy } = useSelector((state: RootState) => state.application.activeDialog);
  const writeBlockedMessage = l10n.getString('forms-rbac-readonly-action-denied');

  useEffect(() => {
    if (id) {
      const device = devices.find((dev) => dev.id === id);
      if (device) {
        dispatch(manageRsuFormReducer.actions.prefillForm(device));
      }
    }
  }, [id]);

  const submitForm = () => {
    const device: NewRSU = {
      location: {
        type: 'Point',
        coordinates: [formState.longitude, formState.latitude],
      },
      name: formState.name,
      roadSegmentId:
        formState.roadSegmentId ??
        (selectedRoadSegmentIds && selectedRoadSegmentIds.length === 1
          ? selectedRoadSegmentIds[0]
          : null),
      id: type === CrudTypeEnum.NEW_RSU ? undefined : formState.id,
    };

    if (isTextValueValid(formState.direction)) {
      device.direction = formState.direction;
    }
    if (isTextValueValid(formState.km)) {
      device.km = formState.km;
    }
    if (isTextValueValid(formState.objectType)) {
      device.type = formState.objectType;
    }
    switch (type) {
      case CrudTypeEnum.NEW_RSU:
        dispatch(devicesSagas.actions.createRSU(device));
        break;
      case CrudTypeEnum.UPDATE_RSU:
        dispatch(
          devicesSagas.actions.updateRSU({
            updatedRSU: device,
            id: device.id,
          }),
        );
        break;
      default:
        break;
    }
    mapRef.panTo(toLatLng(device.location.coordinates));
  };

  const onChangeName = useCallback((ev) => {
    if (!touched.name) {
      setTouched({ ...touched, name: true });
    }
    dispatch(manageRsuFormReducer.actions.nameChanged(ev.target.value));
  }, []);
  const createChangeHandler = (
    field: string,
    validator: (num: number) => boolean,
    actionCreator: (num: number) => any,
  ) =>
    useCallback((ev) => {
      const number = parseFloat(ev.target.value.replace(/,/g, '.'));
      if (validator(number) || !ev.target.value) {
        if (!touched[field]) {
          setTouched({ ...touched, [field]: true });
        }
        return dispatch(actionCreator(number));
      }
      return null;
    }, []);

  const onChangeLatitude = createChangeHandler(
    'latitude',
    isLatitude,
    manageRsuFormReducer.actions.latitudeChanged,
  );
  const onChangeLongitude = createChangeHandler(
    'longitude',
    isLongitude,
    manageRsuFormReducer.actions.longitudeChanged,
  );
  const onChangeRoadSegmentId = useCallback((ev) => {
    console.log('onChangeRoadSegmentId', ev.target.value);
    dispatch(manageRsuFormReducer.actions.roadSegmentIdChanged(ev.target.value));
  }, []);

  return (
    <CardContent className={classes.cardContent}>
      <Tooltip title={l10n.getString('forms-rsu-name')} placement="right">
        <TextField
          id="name-rsu"
          label={l10n.getString('forms-rsu-name')}
          InputLabelProps={{ style: { fontSize: 12 } }}
          value={formState.name ? formState.name?.toString() : ''}
          disabled={busy}
          onChange={onChangeName}
          error={touched.name && !isTextValueValid(formState.name)}
        />
      </Tooltip>
      <Tooltip title={l10n.getString('forms-rsu-lat')} placement="right">
        <TextField
          id="latitude"
          label={l10n.getString('forms-rsu-lat')}
          InputLabelProps={{ style: { fontSize: 12 } }}
          type="number"
          value={isNumberValid(formState.latitude) ? formState.latitude : ''}
          onChange={onChangeLatitude}
          className={classes.inputStyle}
          error={touched.latitude && !isLatitude(formState.latitude)}
        />
      </Tooltip>
      <Tooltip title={l10n.getString('forms-rsu-long')} placement="right">
        <TextField
          id="longitude"
          label={l10n.getString('forms-rsu-long')}
          InputLabelProps={{ style: { fontSize: 12 } }}
          type="number"
          value={isNumberValid(formState.longitude) ? formState.longitude : ''}
          onChange={onChangeLongitude}
          className={classes.inputStyle}
          error={touched.longitude && !isLongitude(formState.longitude)}
        />
      </Tooltip>

      <Tooltip title={l10n.getString('forms-rsu-direction-tp')} placement="right">
        <TextField
          id="direction-rsu"
          label={`${l10n.getString('forms-rsu-direction')} ${l10n.getString('forms-field-optional')}`}
          InputLabelProps={{ style: { fontSize: 13 } }}
          value={formState.direction ?? ''}
          disabled={busy}
          onChange={(ev) =>
            dispatch(manageRsuFormReducer.actions.directionChanged(ev.target.value))
          }
          style={{ marginTop: 10 }}
        />
      </Tooltip>

      <Tooltip title={l10n.getString('forms-rsu-km-tp')} placement="right">
        <TextField
          id="km-rsu"
          label={`${l10n.getString('forms-rsu-km')} ${l10n.getString('forms-field-optional')}`}
          InputLabelProps={{ style: { fontSize: 13 } }}
          value={formState.km ?? ''}
          disabled={busy}
          onChange={(ev) => dispatch(manageRsuFormReducer.actions.kmChanged(ev.target.value))}
          style={{ marginTop: 10 }}
        />
      </Tooltip>

      <Tooltip title={l10n.getString('forms-rsu-type-tp')} placement="right">
        <TextField
          id="type-rsu"
          label={`${l10n.getString('forms-rsu-type')} ${l10n.getString('forms-field-optional')}`}
          InputLabelProps={{ style: { fontSize: 13 } }}
          value={formState.objectType ?? ''}
          disabled={busy}
          onChange={(ev) =>
            dispatch(manageRsuFormReducer.actions.objectTypeChanged(ev.target.value))
          }
          style={{ marginTop: 10, marginBottom: 10 }}
        />
      </Tooltip>

      <SelectItem
        text={l10n.getString('forms-rsu-road')}
        options={roads.map((road) => ({ label: road.name, value: road.id }))}
        value={
          formState.roadSegmentId ??
          (selectedRoadSegmentIds && selectedRoadSegmentIds.length == 1
            ? selectedRoadSegmentIds[0]
            : '')
        }
        onChange={onChangeRoadSegmentId}
        tooltip={l10n.getString('forms-rsu-road')}
      />

      <RoleGuard
        allowed={canManageRsu}
        blockedActionMode={blockedActionMode}
        blockedMessage={writeBlockedMessage}
      >
        <Button
          variant="outlined"
          style={{
            marginTop: 20,
          }}
          disabled={!isFormValid(formState) || busy}
          onClick={submitForm}
        >
          {id != null ? l10n.getString('forms-rsu-update-rsu') : l10n.getString('forms-rsu-add-new')}
        </Button>
      </RoleGuard>
    </CardContent>
  );
};

export default React.memo(RsuManage);
